import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';

import CommonTable from '../../../components/common-table/CommonTable';
import Tr from '../../../components/common-table/components/Tr';
import Th from '../../../components/common-table/components/Th';
import ProjectsTableItem from './ProjectsTableItem';
import useProjectTypeOptions from '../../../hooks/useProjectTypeOptions';
import { changeTableSort } from '../../../redux/projects/projectsReducer';
import calculateSortStr from '../../../utils/calculateSortValue';
import ThWithSort from '../../../components/common-table/components/ThWithSort';
import IsGranted from '../../../components/is-granted/IsGranted';
import { USER_ROLES } from '../../../constants';

import scss from '../ProjectsList.module.scss';

const ProjectsTable = ({
  onOpenProject,
  onShareProject,
  onEditProject,
  onDeleteProject,
  onDeactivateProject,
  onCollectData,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('projects');
  const isLoading = useSelector((state) => state.projects.isLoading);
  const projects = useSelector((state) => state.projects.projects);
  const projectsSort = useSelector((state) => state.projects.tableSettings.sort);

  const projectTypeOptions = useProjectTypeOptions();

  const handleChangeSort = useCallback(
    (sortValue) => {
      dispatch(changeTableSort(calculateSortStr({ currentSort: projectsSort, newSortKey: sortValue })));
    },
    [dispatch, projectsSort],
  );

  return (
    <CommonTable
      content={projects}
      isLoading={isLoading}
      renderBody={(project) => (
        <ProjectsTableItem
          key={project.id}
          project={project}
          projectTypeOptions={projectTypeOptions}
          onOpen={onOpenProject}
          onShare={onShareProject}
          onEdit={onEditProject}
          onDelete={onDeleteProject}
          onDeactivate={onDeactivateProject}
          onCollectData={onCollectData}
          disabled={projectTypeOptions.every(({ value }) => value !== project.projectType)}
        />
      )}>
      <Tr>
        <ThWithSort
          label={t('type')}
          columnKey='projectType'
          sortStr={projectsSort}
          onSort={handleChangeSort}
          extraThClass={scss.cellLeft}
        />
        <ThWithSort
          label={t('name')}
          columnKey='projectName'
          sortStr={projectsSort}
          onSort={handleChangeSort}
          extraThClass={scss.cellLeft}
        />
        <ThWithSort
          label={t('dataSets')}
          columnKey='dataSets'
          sortStr={projectsSort}
          onSort={handleChangeSort}
          extraThClass={clsx(scss.cellLeft, scss.cell100)}
        />
        <ThWithSort
          label={t('createdOn')}
          columnKey='createdOn'
          sortStr={projectsSort}
          onSort={handleChangeSort}
          extraThClass={scss.cellLeft}
        />
        <ThWithSort
          label={t('createdBy')}
          columnKey='createdBy'
          sortStr={projectsSort}
          onSort={handleChangeSort}
          extraThClass={scss.cellLeft}
        />
        <ThWithSort
          label={t('status')}
          columnKey='active'
          sortStr={projectsSort}
          onSort={handleChangeSort}
          extraThClass={clsx(scss.cellLeft, scss.cell100)}
        />
        <IsGranted permissions={USER_ROLES.GLOBAL_ADMIN}>
          <Th extraClasses={scss.cellActions}></Th>
        </IsGranted>
      </Tr>
    </CommonTable>
  );
};

export default ProjectsTable;
